import {
  Autocomplete,
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SearchIcon from "@mui/icons-material/Search";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";
import { useTranslation } from "react-i18next";
import DurationField from "../../common/DurationField";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";
import {
  outlinedFieldSx,
  outlinedFieldMultilineSx,
  autocompleteOptionSx,
  pickerPaperSx,
  searchIconSx,
  warningOutlineSx
} from "./formStyles";

interface Props {
  patientOptions: string[];
  patientName: string;
  onPatientNameChange: (value: string) => void;
  scheduledDateTime: moment.Moment | null;
  onDateChange: (value: moment.Moment | null) => void;
  /** Raw text so the field can be cleared while typing. */
  duration: string;
  onDurationChange: (value: string) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  scheduleDisabled: boolean;
  /** Typed name doesn't match any of the doctor's clinical files. */
  unknownPatient: boolean;
  onSchedule: () => void;
}

const titleSx = {
  ...typography.heading.m,
  color: semanticColors.text.primary,
  mb: 3,
  display: "flex",
  alignItems: "center",
  gap: 1
};

const scheduleBtnSx = (disabled: boolean) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: disabled
    ? semanticColors.surface.container
    : semanticColors.gradient.surface,
  borderRadius: "40px",
  height: "48px",
  width: "100%",
  border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
  px: "24px"
});

const scheduleBtnLabelSx = {
  ...typography.body.lBold,
  color: semanticColors.text.inverse
};

// Same leading search affordance as the history "search by patient" filter.
const searchAdornment = (
  <InputAdornment position="start">
    <SearchIcon sx={searchIconSx} />
  </InputAdornment>
);

const unknownPatientSx = {
  ...typography.body.s,
  color: semanticColors.feedback.danger,
  mt: 1
};

export default function ScheduleForm({
  patientOptions,
  patientName,
  onPatientNameChange,
  scheduledDateTime,
  onDateChange,
  duration,
  onDurationChange,
  notes,
  onNotesChange,
  scheduleDisabled,
  unknownPatient,
  onSchedule
}: Props) {
  const { t } = useTranslation();

  return (
    <Box maxWidth={480}>
      <Typography sx={titleSx}>
        <ScheduleIcon /> {t("videoConsultations.scheduleTitle")}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {patientOptions.length > 0 ? (
            <Autocomplete
              options={patientOptions}
              value={patientName}
              onChange={(_, val) => onPatientNameChange(val || "")}
              onInputChange={(_, val) => onPatientNameChange(val)}
              freeSolo
              componentsProps={{ paper: { sx: autocompleteOptionSx } }}
              sx={outlinedFieldSx}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t("videoConsultations.patientName")}
                  fullWidth
                  required
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {searchAdornment}
                        {params.InputProps.startAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          ) : (
            <TextField
              label={t("videoConsultations.patientName")}
              value={patientName}
              onChange={e => onPatientNameChange(e.target.value)}
              fullWidth
              required
              InputProps={{ startAdornment: searchAdornment }}
              sx={outlinedFieldSx}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <DateTimePicker
            label={t("videoConsultations.dateTimeLabel")}
            value={scheduledDateTime}
            onChange={val => onDateChange(val)}
            disablePast
            localeText={{
              okButtonLabel: t("common.save"),
              cancelButtonLabel: t("common.cancel")
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: { ...outlinedFieldSx, ...warningOutlineSx }
              },
              desktopPaper: { sx: pickerPaperSx },
              mobilePaper: { sx: pickerPaperSx }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <DurationField
            value={duration}
            onChange={onDurationChange}
            sx={outlinedFieldSx}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label={t("videoConsultations.notesLabel")}
            value={notes}
            onChange={e => onNotesChange(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={outlinedFieldMultilineSx}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            component="button"
            onClick={onSchedule}
            disabled={scheduleDisabled}
            sx={scheduleBtnSx(scheduleDisabled)}
          >
            <Typography sx={scheduleBtnLabelSx}>
              {t("videoConsultations.scheduleButton")}
            </Typography>
          </Box>
          {unknownPatient && (
            <Typography sx={unknownPatientSx}>
              {t("videoConsultations.selectFromList")}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
